/**
 * Main Entry Point - Frontend Consolidation
 *
 * Sets up React root rendering for the unified application.
 * Configures development and production modes.
 *
 * Requirements: 1.1
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import './shared/src/index.css';

// Development mode configuration
const isDevelopment = import.meta.env.DEV;

// Error boundary for production
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif'
        }}>
          <h1>Something went wrong</h1>
          <p>The application encountered an unexpected error.</p>
          {isDevelopment && this.state.error && (
            <details style={{ marginTop: '1rem', textAlign: 'left' }}>
              <summary>Error Details (Development Mode)</summary>
              <pre style={{
                background: '#f5f5f5',
                padding: '1rem',
                overflow: 'auto',
                fontSize: '0.875rem'
              }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Get root container
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found. Make sure there is a div with id="root" in your HTML.');
}

// Create React root
const root = createRoot(container);

// Render application with error boundary
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Development mode enhancements
if (isDevelopment) {
  console.log('🚀 Frontend Consolidation - Development Mode');
  console.log('Available routes:');
  console.log('  / - Landing Dashboard');
  console.log('  /admin - User Management (requires admin role)');
  console.log('  /policy-navigator - RAG Chat (requires policy_navigator role)');
  console.log('  /evidence-extractor - Evidence Tool (requires evidence_extractor role)');
}

// Production mode optimizations
if (!isDevelopment) {
  // Disable React DevTools in production
  if (typeof window !== 'undefined') {
    const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (hook) {
      hook.onCommitFiberRoot = undefined;
    }
  }
}
